// App root
import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Index from "./pages/Index";

const Login = lazy(() => import("./pages/Login"));
const Cursos = lazy(() => import("./pages/Cursos"));
const Categorias = lazy(() => import("./pages/Categorias"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const SolucoesCorporativas = lazy(() => import("./pages/SolucoesCorporativas"));
const ParaEmpresas = lazy(() => import("./pages/ParaEmpresas"));
const Sobre = lazy(() => import("./pages/Sobre"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Cadastrar = lazy(() => import("./pages/Cadastrar"));
const CoursePlayer = lazy(() => import("./pages/CoursePlayer"));
const CourseDetail = lazy(() => import("./pages/CourseDetail"));
const LessonPage = lazy(() => import("./pages/LessonPage"));
const Checkout = lazy(() => import("./pages/Checkout"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const StudentArea = lazy(() => import("./pages/StudentArea"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TermosDeUso = lazy(() => import("./pages/TermosDeUso"));
const PoliticaPrivacidade = lazy(() => import("./pages/PoliticaPrivacidade"));
const PoliticaCookies = lazy(() => import("./pages/PoliticaCookies"));
const RecuperarSenha = lazy(() => import("./pages/RecuperarSenha"));
const RedefinirSenha = lazy(() => import("./pages/RedefinirSenha"));
const VerificarCertificado = lazy(() => import("./pages/VerificarCertificado"));
const Contato = lazy(() => import("./pages/Contato"));
const AdminLeads = lazy(() => import("./pages/AdminLeads"));
const AdminTreinamentos = lazy(() => import("./pages/AdminTreinamentos"));
const AdminBiblioteca = lazy(() => import("./pages/AdminBiblioteca"));
const Suporte = lazy(() => import("./pages/Suporte"));

// QueryClient configurado pra UX de plataforma de cursos:
// - refetchOnWindowFocus: false → NÃO recarrega dados quando o aluno volta
//   pra aba (evita re-render que zerava o video do YouTube embedado).
// - staleTime: 5 min → reduz refetches desnecessários durante a aula.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  },
});

const RouteFallback = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <LanguageProvider>
        <AuthProvider>
          <CartProvider>
            <Toaster />
            <Sonner />
            {/* Título padrão do app.
                Sem isto, o <title> setado pelo CourseDetail GRUDA ao sair da
                página: o Helmet não volta pro title estático do index.html,
                mantém o último valor que um Helmet montado definiu. A home
                aparecia na aba como "ISO 22716 – Internal Auditor | DQS
                Academy" (reportado pelo Kaio com print, ago/2026).

                Usar `defaultTitle` e NÃO um <title> filho: defaultTitle só
                entra quando nenhum outro Helmet define título, então não
                disputa com o do CourseDetail. Um <title> filho aqui poderia
                vencer o da página de venda e apagar o título do curso.

                ⚠️ Não dá pra validar isto no dev local: o react-helmet-async
                não emite tag nenhuma aqui (zero elementos [data-rh], mesmo
                sem este Helmet). Em produção funciona. Verificar sempre no
                deploy, não no localhost. */}
            <Helmet defaultTitle="DQS Academy Brasil — Cursos ISO e Auditoria Online" />
            <BrowserRouter>
              <ScrollToTop />
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/cadastrar" element={<Cadastrar />} />
                  <Route path="/register" element={<Cadastrar />} />
                  <Route path="/recuperar-senha" element={<RecuperarSenha />} />
                  <Route path="/redefinir-senha" element={<RedefinirSenha />} />
                  <Route path="/reset-password" element={<RedefinirSenha />} />

                  {/* Legal - public */}
                  <Route path="/termos-de-uso" element={<TermosDeUso />} />
                  <Route path="/politica-de-privacidade" element={<PoliticaPrivacidade />} />
                  <Route path="/politica-de-cookies" element={<PoliticaCookies />} />
                  <Route path="/terms-of-use" element={<TermosDeUso />} />
                  <Route path="/privacy-policy" element={<PoliticaPrivacidade />} />
                  <Route path="/cookie-policy" element={<PoliticaCookies />} />
                  <Route path="/verificar/:codigo" element={<VerificarCertificado />} />

                  {/* Public routes */}
                  <Route path="/" element={<Index />} />
                  <Route path="/cursos" element={<Cursos />} />
                  <Route path="/catalogo" element={<Cursos />} />
                  <Route path="/explorar" element={<Cursos />} />
                  <Route path="/courses" element={<Cursos />} />
                  <Route path="/catalog" element={<Cursos />} />
                  <Route path="/explore" element={<Cursos />} />

                  <Route path="/categorias" element={<Categorias />} />
                  <Route path="/categorias/:categoryId" element={<Categorias />} />
                  <Route path="/qualidade" element={<CategoryPage />} />
                  <Route path="/meio-ambiente" element={<CategoryPage />} />
                  <Route path="/seguranca-do-trabalho" element={<CategoryPage />} />
                  <Route path="/seguranca-da-informacao" element={<CategoryPage />} />
                  <Route path="/automotivo" element={<CategoryPage />} />
                  <Route path="/categories" element={<Categorias />} />
                  <Route path="/categories/:categoryId" element={<Categorias />} />
                  <Route path="/quality" element={<CategoryPage />} />
                  <Route path="/environment" element={<CategoryPage />} />
                  <Route path="/occupational-health-safety" element={<CategoryPage />} />
                  <Route path="/information-security" element={<CategoryPage />} />
                  <Route path="/automotive" element={<CategoryPage />} />

                  <Route path="/solucoes-corporativas" element={<SolucoesCorporativas />} />
                  <Route path="/empresas" element={<ParaEmpresas />} />
                  <Route path="/corporate-solutions" element={<SolucoesCorporativas />} />
                  <Route path="/companies" element={<ParaEmpresas />} />

                  <Route path="/sobre" element={<Sobre />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/about" element={<Sobre />} />

                  <Route path="/contato" element={<Contato />} />
                  <Route path="/contact" element={<Contato />} />
                  <Route path="/contacto" element={<Contato />} />

                  <Route path="/suporte" element={<Suporte />} />
                  <Route path="/support" element={<Suporte />} />
                  <Route path="/soporte" element={<Suporte />} />

                  <Route path="/termos" element={<TermosDeUso />} />
                  <Route path="/privacidade" element={<PoliticaPrivacidade />} />

                  <Route path="/curso/:slug" element={<CourseDetail />} />
                  <Route path="/course/:slug" element={<CourseDetail />} />

                  {/* Protected routes */}
                  <Route path="/checkout/success" element={<CheckoutSuccess />} />
                  <Route path="/pagamento-sucesso" element={<CheckoutSuccess />} />
                  <Route path="/payment-success" element={<CheckoutSuccess />} />
                  <Route path="/checkout/:courseId" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />

                  <Route path="/aprender/:courseId" element={<ProtectedRoute><CoursePlayer /></ProtectedRoute>} />
                  <Route path="/learn/:courseId" element={<ProtectedRoute><CoursePlayer /></ProtectedRoute>} />

                  <Route path="/cursos/:courseId/aula" element={<ProtectedRoute><LessonPage /></ProtectedRoute>} />
                  <Route path="/cursos/:courseId/aula/:lessonId" element={<ProtectedRoute><LessonPage /></ProtectedRoute>} />

                  <Route path="/minha-area" element={<ProtectedRoute><StudentArea /></ProtectedRoute>} />
                  <Route path="/meus-cursos" element={<ProtectedRoute><StudentArea /></ProtectedRoute>} />
                  <Route path="/perfil" element={<ProtectedRoute><StudentArea /></ProtectedRoute>} />
                  <Route path="/certificados" element={<ProtectedRoute><StudentArea /></ProtectedRoute>} />
                  <Route path="/my-courses" element={<ProtectedRoute><StudentArea /></ProtectedRoute>} />
                  <Route path="/profile" element={<ProtectedRoute><StudentArea /></ProtectedRoute>} />
                  <Route path="/certificates" element={<ProtectedRoute><StudentArea /></ProtectedRoute>} />

                  <Route path="/admin/leads" element={<ProtectedRoute><AdminLeads /></ProtectedRoute>} />
                  <Route path="/admin/treinamentos" element={<ProtectedRoute><AdminTreinamentos /></ProtectedRoute>} />
                  <Route path="/admin/biblioteca" element={<ProtectedRoute><AdminBiblioteca /></ProtectedRoute>} />
                  <Route path="/:categorySlug" element={<CategoryPage />} />

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </CartProvider>
        </AuthProvider>
      </LanguageProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
